import api from './api';

export interface WhatsappConfig {
  id?: number;
  instanceId: string;
  apiKey: string;
  groupId?: string;
  isActive: boolean;
}

export interface WhatsappTemplate {
  id?: number;
  name: string;
  type:
    | 'bill_reminder'
    | 'payment_confirmed'
    | 'payment_created'
    | 'overdue_notice'
    | 'reminder_7_days'
    | 'reminder_3_days'
    | 'reminder_1_day'
    | 'reminder_due_today'
    | 'custom';
  template: string;
  status?: string | null;
  spop_id?: string | null;
  isActive?: boolean;
}

export interface WhatsappGroup {
  id: string;
  name?: string;
  subject?: string;
  /** Nilai untuk field `to` saat kirim ke StarSender (biasanya nama grup) */
  sendTarget?: string;
  participants?: number;
  [key: string]: any;
}

export interface SendMessageRequest {
  phone?: string;
  message: string;
  sendToGroup?: boolean;
  /** Kirim langsung tanpa antrian/jadwal (untuk pesan test) */
  immediate?: boolean;
}

export interface BroadcastRequest {
  spop_ids: string[];
  message: string;
  customer_status?: string;
  priority?: number;
  scheduled_at?: string;
  send_immediately?: boolean;
}

export interface SpopInfo {
  spop_id: string;
  spop_name: string;
  spop_area_code: string;
  customer_count: number;
}

export interface BroadcastCustomer {
  id: string;
  nama_customer: string;
  no_whatsapp: string;
  status: string;
  spop_id: string;
  spop_name?: string;
}

export interface BroadcastHistory {
  id: number;
  phone: string;
  message: string;
  scheduled_at: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  priority: number;
  template_type: string;
  related_id: string;
  retry_count: number;
  max_retries?: number;
  error_message?: string;
  sent_at?: string;
  createdAt: string;
}

export interface BroadcastStats {
  totalBroadcasts: number;
  pendingBroadcasts: number;
  sentBroadcasts: number;
  failedBroadcasts: number;
  broadcastsLastHour: number;
  broadcastsLastDay: number;
  broadcastsLastWeek: number;
}

export interface WhatsappQueueItem {
  id: number;
  phone: string | null;
  group_id: string | null;
  message: string;
  scheduled_at: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  priority: number;
  template_type: string | null;
  related_id: string | null;
  retry_count: number;
  max_retries: number;
  error_message?: string | null;
  sent_at?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface QueueLogStats {
  totalPending: number;
  totalSent: number;
  totalFailed: number;
  sentLastHour: number;
  sentLastDay: number;
}

class WhatsappService {
  private baseURL = '/whatsapp';

  // Configuration methods
  async getConfig(): Promise<WhatsappConfig> {
    const response = await api.get(`${this.baseURL}/config`);
    return response.data.data;
  }

  async saveConfig(config: Omit<WhatsappConfig, 'id'>): Promise<WhatsappConfig> {
    const response = await api.post(`${this.baseURL}/config`, config);
    return response.data.data;
  }

  async testConnection(): Promise<any> {
    const response = await api.post(`${this.baseURL}/test-connection`);
    return response.data;
  }

  // Groups methods
  async getGroups(apiKey?: string): Promise<{ success: boolean; data?: WhatsappGroup[]; message?: string; error?: string }> {
    const params: Record<string, string> = {};
    if (apiKey && !apiKey.startsWith('****')) {
      params.apiKey = apiKey;
    }
    const response = await api.get(`${this.baseURL}/groups`, { params });
    return response.data;
  }

  // Template methods
  async getTemplates(): Promise<WhatsappTemplate[]> {
    const response = await api.get(`${this.baseURL}/templates`);
    return response.data.data;
  }

  async createTemplate(template: Omit<WhatsappTemplate, 'id'>): Promise<WhatsappTemplate> {
    // Convert undefined to null for backend compatibility
    const templateData = {
      ...template,
      status: template.status || null,
      spop_id: template.spop_id || null
    };
    
    const response = await api.post(`${this.baseURL}/templates`, templateData);
    return response.data.data;
  }

  async updateTemplate(id: number, template: Partial<WhatsappTemplate>): Promise<WhatsappTemplate> {
    // Convert undefined to null for backend compatibility
    const templateData = {
      ...template,
      status: template.status !== undefined ? (template.status || null) : undefined,
      spop_id: template.spop_id !== undefined ? (template.spop_id || null) : undefined
    };
    
    const response = await api.put(`${this.baseURL}/templates/${id}`, templateData);
    return response.data.data;
  }

  async deleteTemplate(id: number): Promise<void> {
    await api.delete(`${this.baseURL}/templates/${id}`);
  }

  // Message methods
  async sendCustomMessage(request: SendMessageRequest): Promise<any> {
    const response = await api.post(`${this.baseURL}/send-message`, request);
    return response.data;
  }

  // Get template for specific customer
  async getTemplateForCustomer(type: string, customerStatus?: string, customerSpopId?: string): Promise<WhatsappTemplate> {
    const params = new URLSearchParams({ type });
    if (customerStatus) params.append('customerStatus', customerStatus);
    if (customerSpopId) params.append('customerSpopId', customerSpopId);
    
    const response = await api.get(`${this.baseURL}/templates/for-customer?${params}`);
    return response.data.data;
  }

  // Get template statistics
  async getTemplateStats(): Promise<any[]> {
    const response = await api.get(`${this.baseURL}/templates/stats`);
    return response.data.data;
  }

  // Broadcast methods
  async getAvailableSpops(status?: string): Promise<SpopInfo[]> {
    const params: Record<string, string> = {};
    if (status && status !== 'all') {
      params.status = status;
    }
    const response = await api.get(`${this.baseURL}/broadcast/spops`, { params });
    return response.data.data;
  }

  async getCustomersBySpop(spop_id: string, page = 1, limit = 20, status?: string): Promise<{
    customers: BroadcastCustomer[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const params: Record<string, string | number> = { spop_id, page, limit };
    if (status && status !== 'all') {
      params.status = status;
    }
    const response = await api.get(`${this.baseURL}/broadcast/customers`, { params });
    return response.data.data;
  }

  async sendBroadcast(request: BroadcastRequest): Promise<any> {
    const response = await api.post(`${this.baseURL}/broadcast/send`, request);
    return response.data;
  }

  async getBroadcastHistory(page = 1, limit = 20, status?: string): Promise<{
    history: BroadcastHistory[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
    summary: Record<string, number>;
  }> {
    const params: Record<string, string | number> = { page, limit };
    if (status && status !== 'all') {
      params.status = status;
    }
    const response = await api.get(`${this.baseURL}/broadcast/history`, { params });
    return response.data.data;
  }

  async getBroadcastStats(): Promise<BroadcastStats> {
    const response = await api.get(`${this.baseURL}/broadcast/stats`);
    return response.data.data;
  }

  /** Batalkan satu item antrian broadcast (by queue id) */
  async cancelBroadcast(queueId: number | string): Promise<any> {
    const response = await api.post(`${this.baseURL}/broadcast/cancel/${queueId}`);
    return response.data;
  }

  // Queue log methods
  async getQueueLog(params: {
    page?: number;
    limit?: number;
    status?: string;
    template_type?: string;
  } = {}): Promise<{
    queue: WhatsappQueueItem[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
    summary: Record<string, number>;
  }> {
    const response = await api.get(`${this.baseURL}/queue/status`, { params });
    return response.data.data;
  }

  async getQueueLogStats(): Promise<QueueLogStats> {
    const response = await api.get(`${this.baseURL}/queue/stats`);
    return response.data.data;
  }

  async cancelQueueMessage(id: number): Promise<any> {
    const response = await api.post(`${this.baseURL}/queue/${id}/cancel`);
    return response.data;
  }

  async processQueue(): Promise<any> {
    const response = await api.post(`${this.baseURL}/queue/process`);
    return response.data;
  }
}

export default new WhatsappService();
